import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/states';

/**
 * "No active deliveries" and "the feed is broken" must never render the same
 * shape — the same discipline `Fleet/components/PanelResult.tsx` was built
 * for, and reused here rather than duplicated with different judgment calls,
 * except for the reasoning below which is specific to this epic.
 *
 * Under `tarabeeza_app` RLS a read with no tenant/branch scope bound answers
 * ZERO ROWS, not an error, and `DeliveryOrderListener.safely()` on the
 * backend deliberately swallows every creation failure into a log line rather
 * than surfacing it anywhere a dashboard could read — so a dispatch board
 * that renders a broken poll as "no active deliveries" is not a cosmetic bug,
 * it is a board that goes quiet while orders are actually piling up
 * unassigned. A bordered, tinted alert with the server's own words and a
 * Retry is unmistakable for a list that is short on purpose.
 */
export interface PanelResultProps {
  loading: boolean;
  error: string | null;
  count: number;
  onRetry: () => void;
  /** Names the thing that failed to load: "the dispatch board", "driver shifts". */
  subject: string;
  filtered?: boolean;
  onClearFilters?: () => void;
  emptyTitle: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  children: ReactNode;
}

export function PanelResult({
  loading,
  error,
  count,
  onRetry,
  subject,
  filtered,
  onClearFilters,
  emptyTitle,
  emptyHint,
  emptyAction,
  children,
}: PanelResultProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-300 bg-red-50/70 p-6 text-center dark:border-red-900 dark:bg-red-950/30"
      >
        <div className="grid place-items-center gap-2">
          <Icon as={TriangleAlert} size="xl" className="text-red-600 dark:text-red-400" />
          <p className="text-sm font-medium">{t('delivery.states.loadFailed', { subject })}</p>
          <p className="max-w-lg text-sm text-ink-muted">{error}</p>
          <p className="max-w-lg text-xs text-ink-muted">{t('delivery.states.loadFailedHint')}</p>
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
            <Icon as={RefreshCw} />
            {t('common.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  if (count === 0 && filtered) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={SearchX} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{t('delivery.states.noMatches')}</p>
        <p className="max-w-sm text-sm text-ink-muted">{t('delivery.states.noMatchesHint')}</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t('delivery.states.clearFilters')}
          </Button>
        )}
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="grid place-items-center gap-2 rounded-xl border border-dashed py-16 text-center">
        <Icon as={Inbox} size="xl" className="text-line-strong" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyHint && <p className="max-w-sm text-sm text-ink-muted">{emptyHint}</p>}
        {emptyAction}
      </div>
    );
  }

  return <>{children}</>;
}

export default PanelResult;
